/**
 * Client-side Image Compression Utility
 * 
 * Automatically compresses large iPhone & mobile photos (5MB - 6MB) down to
 * sharp, high-performance WebP (~80KB - 150KB) in the browser before uploading.
 * Eliminates slow upload times and keeps cloud storage clean.
 */

export interface CompressOptions {
  maxWidth?: number; // default: 1200px (ideal for retina e-commerce product cards)
  maxHeight?: number; // default: 1200px
  quality?: number; // default: 0.80 (crisp quality, 95%+ size reduction)
}

export async function compressImageFile(
  file: File,
  options: CompressOptions = {}
): Promise<File> {
  const { maxWidth = 1200, maxHeight = 1200, quality = 0.80 } = options;

  // Do not compress non-image files, SVGs, or animated GIFs
  if (
    !file ||
    !file.type.startsWith('image/') ||
    file.type.includes('svg') ||
    file.type.includes('gif')
  ) {
    return file;
  }

  // If already under 120KB and WebP, keep as is
  if (file.size <= 120 * 1024 && file.type === 'image/webp') {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Constrain dimensions maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(file);
          return;
        }

        // Draw image resized
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to WebP blob
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) {
              // If compression didn't save bytes, fallback to original
              resolve(file);
              return;
            }

            const rawName = file.name;
            const dotIdx = rawName.lastIndexOf('.');
            const baseName = dotIdx !== -1 ? rawName.substring(0, dotIdx) : rawName;

            const compressedFile = new File([blob], `${baseName}.webp`, {
              type: 'image/webp',
              lastModified: Date.now(),
            });

            resolve(compressedFile);
          },
          'image/webp',
          quality
        );
      };

      img.onerror = () => resolve(file);
      img.src = event.target?.result as string;
    };

    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export async function compressImageFiles(
  files: File[],
  options?: CompressOptions
): Promise<File[]> {
  if (!files || files.length === 0) return [];
  return Promise.all(files.map((file) => compressImageFile(file, options)));
}
